import type { StartupData } from '@workspace/api-backend'
import { Badge } from '@workspace/ui/components/badge'
import { Banner } from '@workspace/ui/components/banner'
import { Button } from '@workspace/ui/components/button'
import { LabelValue } from '@workspace/ui/components/label-value'
import { MetricCard } from '@workspace/ui/components/metric-card'
import { ProfileHeader } from '@workspace/ui/components/profile-header'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { formatCurrency, formatDate } from '@workspace/ui/lib/utils'
import {
  Calendar,
  EyeOff,
  Globe,
  Layers,
  MapPin,
  Pencil,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import {
  useStartupConfigQuery,
  useStartupDocumentsQuery,
  useStartupMembersQuery,
} from '../../../hooks/api'
import { DocumentPreviewModal, type PreviewDocument } from '../../preview'
import { DocumentsList } from './documents-list'
import { TeamList } from './team-list'

type StartupViewProps = {
  startup: StartupData
  canEdit: boolean
  onEdit?: () => void
}

export function StartupView({ startup, canEdit, onEdit }: StartupViewProps) {
  const { data: configResponse } = useStartupConfigQuery()

  const { data: documentsData } = useStartupDocumentsQuery(startup.id)
  const documents = documentsData?.data ?? []

  const { data: membersData } = useStartupMembersQuery(startup.id)
  const members = membersData?.data ?? []

  const productStages = configResponse?.data?.productStages ?? []
  const raiseStages = configResponse?.data?.raiseStages ?? []

  const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null)

  if (startup.isStealth && !canEdit) {
    return (
      <ProfileHeader
        name="Stealth Startup"
        subtitle="This startup is in stealth mode"
        badges={
          <Badge variant="secondary" size="md" className="gap-1">
            <EyeOff className="size-3" />
            Stealth
          </Badge>
        }
        contentClassName="mx-auto max-w-6xl"
      />
    )
  }

  const stageData = startup.productStage
    ? productStages.find((s) => s.value === startup.productStage)
    : null

  return (
    <>
      <ProfileHeader
        coverImageUrl={startup.coverImageUrl}
        avatarUrl={startup.logoUrl}
        name={startup.name}
        subtitle={startup.category?.name}
        badges={
          startup.isStealth ? (
            <Badge variant="secondary" size="md" className="gap-1">
              <EyeOff className="size-3" />
              Stealth
            </Badge>
          ) : undefined
        }
        actions={
          canEdit && onEdit ? (
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="size-4" />
              Edit Profile
            </Button>
          ) : undefined
        }
        contentClassName="mx-auto max-w-6xl"
      />

      <div className="mx-auto w-full max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex flex-wrap items-center gap-6">
            <LabelValue
              icon={<MapPin className="size-4" />}
              label="Location"
              value={startup.location}
              direction="horizontal"
            />
            {startup.websiteUrl && (
              <LabelValue
                icon={<Globe className="size-4" />}
                label="Website"
                value={startup.websiteUrl}
                direction="horizontal"
              />
            )}
            <LabelValue
              icon={<Calendar className="size-4" />}
              label="Founded"
              value={formatDate(startup.foundingDate)}
              direction="horizontal"
            />
            <LabelValue
              icon={<Calendar className="size-4" />}
              label="Verified"
              value={formatDate(startup.verificationDate)}
              direction="horizontal"
            />
          </div>
          {startup.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 ml-auto">
              {startup.tags.map((tag) => (
                <Badge key={tag} variant="secondary" size="md">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl space-y-6">
        {startup.description && (
          <ProfileSection>
            <p className="text-white leading-relaxed">{startup.description}</p>
          </ProfileSection>
        )}

        {startup.isFundraising && (
          <Banner
            variant="success"
            icon={<Sparkles className="size-5" />}
            title="Actively Raising"
            description="This startup is currently seeking investment"
            actions={
              (startup.fundraisingAmount || startup.fundraisingStage) && (
                <div className="flex items-center gap-4 text-right">
                  {startup.fundraisingStage && (
                    <div>
                      <p className="text-xs text-muted-foreground">Round</p>
                      <p className="text-lg font-bold text-emerald-400">
                        {raiseStages.find((s) => s.value === startup.fundraisingStage)?.label ??
                          startup.fundraisingStage}
                      </p>
                    </div>
                  )}
                  {startup.fundraisingAmount && (
                    <div>
                      <p className="text-xs text-muted-foreground">Target</p>
                      <p className="text-lg font-bold text-white">
                        {formatCurrency(startup.fundraisingAmount)}
                      </p>
                    </div>
                  )}
                </div>
              )
            }
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetricCard
            variant="highlight"
            icon={<Target className="size-5" />}
            label={startup.northStarMetricName ?? 'North Star Metric'}
            value={startup.northStarMetricValue ?? '—'}
          />
          <MetricCard
            icon={<TrendingUp className="size-5" />}
            label="ARR"
            value={formatCurrency(startup.arr)}
          />
          <MetricCard
            icon={<Layers className="size-5" />}
            label="Product Stage"
            value={stageData?.label ?? '—'}
          />
        </div>

        <ProfileSection title="Team">
          <TeamList members={members} />
        </ProfileSection>

        <ProfileSection title="Documents">
          <DocumentsList
            documents={documents}
            onPreview={(doc) =>
              setPreviewDocument({
                id: doc.documentId,
                fileName: doc.document.fileName,
                mimeType: doc.document.mimeType,
              })
            }
          />
        </ProfileSection>
      </div>

      {previewDocument && (
        <DocumentPreviewModal
          document={previewDocument}
          open
          onOpenChange={(open) => !open && setPreviewDocument(null)}
        />
      )}
    </>
  )
}
